// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/FindVertexFromGeneralTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview, parseFractionLikeInput } from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: FindVertexFromGeneralTask
   Cel: z f(x)=ax^2+bx+c policz współrzędne wierzchołka (p, q).
   Wejście: FractionInput dla p oraz FractionInput dla q.
   Zasady:
   - ujemne liczby w nawiasach w podstawieniach,
   - w wyjaśnieniach pokazujemy pośrednie wyniki: b^2, 4·a, 4ac, 2a itd.
   - wynik w ułamku zwykłym/mieszanym jak w innych zadaniach.
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}

// format mieszany / zwykły
function formatFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const sign = Math.sign(numerator) * Math.sign(denominator) || 1;
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);
  const N = absNum / d;
  const D = absDen / d;

  if (N % D === 0) return `${sign * (N / D)}`;
  const whole = Math.floor(N / D);
  const rem = N % D;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${N}}{${D}}`;
  }
  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${rem}}{${D}}`;
}

function simplifyFrac(n, d) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const s = Math.sign(n) * Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s * (an / g), d: ad / g };
}

/* ---------- generator ---------- */

export function generateTask() {
  const a = randInt(-5, 5, new Set([0]));
  const b = randInt(-9, 9);
  const c = randInt(-9, 9);

  const delta = b*b - 4*a*c;

  // p = -b / (2a), q = -Δ / (4a)
  const Pfrac = simplifyFrac(-b, 2 * a);
  const Qfrac = simplifyFrac(-delta, 4 * a);

  return {
    type: "FindVertexFromGeneralTask",
    data: {
      a, b, c, delta,
      p: Pfrac.n / Pfrac.d,
      q: Qfrac.n / Qfrac.d,
      Pfrac, Qfrac
    }
  };
}

/* ---------- render ---------- */

export function renderTask({ data }) {
  const { a, b, c } = data;

  const term = (coeff, base, omitOne=false) => {
    if (coeff === 0) return "";
    const sign = coeff > 0 ? " + " : " - ";
    const abs = Math.abs(coeff);
    const num = (abs === 1 && omitOne) ? "" : String(abs);
    return `${sign}${num}${base}`;
  };
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let body = `${aCoeff}x^2${term(b,"x",true)}`;
  if (c !== 0) body += (c > 0 ? " + " : " - ") + String(Math.abs(c));

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej: <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Oblicz współrzędne wierzchołka paraboli <InlineMath math={'(p,\\; q)'} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const merge = (ua) => (ua && typeof ua === "object" && !Array.isArray(ua) ? ua : {});
  const norm  = (v)  => (v && typeof v === "object" && "raw" in v ? v : { raw: String(v ?? "") });

  const current = merge(userAnswer);
  const pVal = norm(current.p);
  const qVal = norm(current.q);

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'p ='} />
        <FractionInput
          value={pVal}
          onChange={(v) => onChange({ ...merge(userAnswer), p: v })}
          disabled={disabled}
          label="p"
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'q ='} />
        <FractionInput
          value={qVal}
          onChange={(v) => onChange({ ...merge(userAnswer), q: v })}
          disabled={disabled}
          label="q"
        />
      </div>
    </div>
  );
}

// >>>>>>>>>>>>>>>>>>>>>>>>> /FIXED <<<<<<<<<<<<<<<<<<<<<<<<

export function checkAnswer({ data }, userAnswer) {
  try {
    if (!userAnswer) return false;

    const toRaw = (x) => x?.raw ?? x ?? "";

    // parser odporny na kształt zwrotu parseFractionLikeInput
    const parseSmart = (raw) => {
      const r = parseFractionLikeInput(toRaw(raw));
      if (r == null) return null;
      if (typeof r === "number") return Number.isFinite(r) ? r : null;
      if (typeof r === "object" && r.parsed != null) {
        return Number.isFinite(r.parsed) ? r.parsed : null;
      }
      // awaryjnie: prosty parser ułamków/mieszanych
      const s = String(toRaw(raw)).trim().replace(/\u2212/g, "-").replace(",", ".");
      let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
      if (m) {
        const A = parseInt(m[1],10), B = parseInt(m[2],10), C = parseInt(m[3],10);
        if (!C) return null;
        const frac = B / C;
        return A >= 0 ? (A + frac) : (A - frac);
      }
      m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
      if (m) {
        const P = parseInt(m[1],10), Q = parseInt(m[2],10);
        if (!Q) return null;
        return P / Q;
      }
      if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
      return null;
    };

    const pGiven = parseSmart(userAnswer.p);
    const qGiven = parseSmart(userAnswer.q);

    if (pGiven == null || qGiven == null) return false;

    return Math.abs(pGiven - data.p) < 0.01 && Math.abs(qGiven - data.q) < 0.01;
  } catch {
    return false;
  }
}


export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data && typeof x.data === "object";
  const task = looksLikeTask(arg1) ? arg1 : (looksLikeTask(arg2) ? arg2 : { data: {} });
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const praw = userAnswer?.p?.raw ?? userAnswer?.p ?? "";
  const qraw = userAnswer?.q?.raw ?? userAnswer?.q ?? "";

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`p = ${formatLatexPreview(praw) || "?"}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`q = ${formatLatexPreview(qraw) || "?"}`} />
    </div>
  );
}


export function renderAnswer({ data }) {
  const { Pfrac, Qfrac } = data;
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`p = ${formatFraction(Pfrac.n, Pfrac.d)},\\quad q = ${formatFraction(Qfrac.n, Qfrac.d)}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, b, c, delta, Pfrac, Qfrac } = data;

  const par = (v) => (v < 0 ? `(${v})` : `${v}`);

  const twoA  = 2 * a;
  const fourA = 4 * a;
  const b2    = b * b;
  const fourAC = 4 * a * c;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Wzory (z karty wzorów):</div>
      <BlockMath math={'p = -\\dfrac{b}{2a},\\quad \\Delta = b^2 - 4ac,\\quad q = -\\dfrac{\\Delta}{4a}'} />

      <div><strong>Krok 2.</strong> Obliczenie <InlineMath math={'p'} />:</div>
      <BlockMath math={`p = -\\dfrac{${par(b)}}{2\\cdot${par(a)}} = -\\dfrac{${b}}{${twoA}} = ${Pfrac.n < 0 ? '-' : ''}${Math.abs(Pfrac.n) % Pfrac.d === 0 ? Math.abs(Pfrac.n)/Pfrac.d : `\\dfrac{${Math.abs(Pfrac.n)}}{${Pfrac.d}}`}`} />

      <div><strong>Krok 3.</strong> Obliczenie <InlineMath math={'\\Delta'} />:</div>
      <BlockMath math={`\\Delta = ${par(b)}^{2} - 4\\cdot${par(a)}\\cdot${par(c)} = ${b2} - ${fourAC < 0 ? `(${fourAC})` : `${fourAC}`} = ${delta}`} />

      <div><strong>Krok 4.</strong> Obliczenie <InlineMath math={'q'} />:</div>
      <BlockMath math={`q = -\\dfrac{${delta}}{4\\cdot${par(a)}} = -\\dfrac{${delta}}{${fourA}} = ${Qfrac.n < 0 ? '-' : ''}${Math.abs(Qfrac.n) % Qfrac.d === 0 ? Math.abs(Qfrac.n)/Qfrac.d : `\\dfrac{${Math.abs(Qfrac.n)}}{${Qfrac.d}}`}`} />

      <div><strong>Wynik:</strong></div>
      <BlockMath math={`(p,\\; q) = \\Big(${Pfrac.n < 0 ? '-' : ''}${Math.abs(Pfrac.n) % Pfrac.d === 0 ? Math.abs(Pfrac.n)/Pfrac.d : `\\dfrac{${Math.abs(Pfrac.n)}}{${Pfrac.d}}`},\\; ${Qfrac.n < 0 ? '-' : ''}${Math.abs(Qfrac.n) % Qfrac.d === 0 ? Math.abs(Qfrac.n)/Qfrac.d : `\\dfrac{${Math.abs(Qfrac.n)}}{${Qfrac.d}}`}\\Big)`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
